import { useState, useEffect } from "react";
import { ProSidebar, Menu, MenuItem, SidebarContent, SidebarFooter } from "react-pro-sidebar";
import { Avatar, Box, Button, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import CategoryIcon from "@mui/icons-material/Category";
// import FastfoodIcon from "@mui/icons-material/Fastfood";
import KebabDiningIcon from "@mui/icons-material/KebabDining";
import ChecklistIcon from "@mui/icons-material/Checklist";
// import IcecreamIcon from "@mui/icons-material/Icecream";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
// import LiquorIcon from "@mui/icons-material/Liquor";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import { useResponsive } from "../hooks/uiHook";
import { AltRoute } from "@mui/icons-material";
import StyleIcon from "@mui/icons-material/Style";
import GroupsIcon from "@mui/icons-material/Groups";
import SideBarItem from "../components/sideBarItem";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CollectionsIcon from "@mui/icons-material/Collections";
import DiscountIcon from "@mui/icons-material/Discount";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchAuthUser, selectAuthUser } from "../features/authSlice";
import { getUserRoleLabel, UserRole } from "../models/user.model";
import { useTranslation } from "react-i18next";
import { isRtlLanguage } from "../i18n";
import { useRestaurant } from "../hooks/RestaurantContext";
import AlertDialog from "../components/alertDialog";

const getInitials = (fullName) => {
  if (!fullName) return "";
  const words = fullName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const first = words[0].charAt(0);
  const last = words[words.length - 1].charAt(0);
  return `${first}${last}`.toUpperCase();
};

const Sidebar = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { isSmallScreen } = useResponsive();
  const [isCollapsed, setIsCollapsed] = useState(isSmallScreen);
  const [changeRestaurantOpen, setChangeRestaurantOpen] = useState(false);
  const { setRestaurantId, setRestaurantName, setRestaurantLogo, setCurrency } =
    useRestaurant();

  useEffect(() => {
    setIsCollapsed(isSmallScreen);
  }, [isSmallScreen]);
  const user = useSelector(selectAuthUser);

  useEffect(() => {
    if (!user) dispatch(fetchAuthUser());
  }, [dispatch, user]);

  const canSeeRestaurants =
    user?.role === UserRole.admin ||
    (user?.role === UserRole.manager &&
      Array.isArray(user.restaurants) &&
      user.restaurants.length > 1);

  const handleChangeRestaurant = () => {
    setChangeRestaurantOpen(false);
    setRestaurantId("");
    setRestaurantName("");
    setRestaurantLogo("");
    setCurrency("");
    navigate("/restaurants");
  };

  return (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: `${colors.primary[400]} !important`,
          borderInlineEnd: `1px solid ${theme.palette.divider}`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          margin: "0 12px !important",
          padding: "8px 12px !important",
          borderRadius: "8px",
          transition: "background-color 0.2s ease, color 0.2s ease",
          overflow: "hidden",
        },
        "& .pro-inner-item .pro-item-content": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
        "& .pro-inner-item:hover": {
          color: `${colors.redAccent[500]} !important`,
          backgroundColor: `${colors.redAccent[500]}1a !important`,
        },
        "& .pro-inner-item:hover .sidebar-item-title": {
          color: `${colors.redAccent[500]} !important`,
        },
        "& .pro-inner-item:hover .pro-icon-wrapper svg": {
          color: `${colors.redAccent[500]} !important`,
        },
        "& .pro-menu-item.active": {
          color: `${colors.redAccent[500]} !important`,
        },
        "& .pro-sidebar.collapsed": {
          width: "80px !important",
          minWidth: "80px !important",
        },
        "& .pro-sidebar.collapsed .pro-inner-item": {
          padding: "8px 12px !important",
        },
      }}
      className="sidebar"
    >
      <Box sx={{ height: "100vh", display: "flex" }}>
        <Box>
          <ProSidebar collapsed={isCollapsed} rtl={isRtlLanguage(i18n.language)}>
            <SidebarContent>
            <Menu iconShape="square">
              <MenuItem
                onClick={() =>
                  setIsCollapsed(isSmallScreen ? true : !isCollapsed)
                }
                icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
                style={{
                  margin: "10px 0 20px 0",
                  color: colors.grey[100],
                }}
              >
                {!isCollapsed && (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    ml="15px"
                  >
                    <Typography variant="h3" color={colors.grey[100]}>
                      {t("nav.dashboard")}
                    </Typography>
                    <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                      <MenuOutlinedIcon />
                    </IconButton>
                  </Box>
                )}
              </MenuItem>

              {!isCollapsed && user && (
                <Box mb="25px">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: `${colors.redAccent[500]}1a`,
                        color: colors.redAccent[500],
                        fontWeight: "bold",
                        fontSize: "1.5rem",
                      }}
                    >
                      {getInitials(user.fullName || t("common.adminName"))}
                    </Avatar>
                  </Box>
                  <Box textAlign="center">
                    <Typography
                      variant="h4"
                      color={colors.grey[100]}
                      fontWeight="bold"
                      sx={{ m: "10px 0 0 0" }}
                    >
                      {user.fullName ? user.fullName : t("common.adminName")}
                    </Typography>
                    <Typography variant="h5" color={colors.redAccent[500]}>
                      {getUserRoleLabel(user.role)}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box paddingLeft={isCollapsed ? undefined : "4px"}>
                  <SideBarItem
                    title={t("nav.home")}
                    to="/dashboard"
                    icon={<HomeOutlinedIcon />}
                  />
                  {!isCollapsed && (
                  <Typography
                    variant="h6"
                    color={colors.grey[300]}
                    sx={{ m: "15px 0 5px 20px" }}
                  >
                    {t("nav.catalogue")}
                  </Typography>
                  )}
                  <SideBarItem
                    title={t("nav.categories")}
                    to="/category"
                    icon={<CategoryIcon />}
                  />
                  <SideBarItem
                    title={t("nav.products")}
                    to="/products"
                    icon={<LocalDiningIcon />}
                  />
                  <SideBarItem
                    title={t("nav.typeVariations")}
                    to="/typeVariation"
                    icon={<StyleIcon />}
                  />
                  <SideBarItem
                    title={t("nav.variation")}
                    to="/variation"
                    icon={<AltRoute />}
                  />
                  {!isCollapsed && (
                  <Typography
                    variant="h6"
                    color={colors.grey[300]}
                    sx={{ m: "15px 0 5px 20px" }}
                  >
                    {t("nav.addons")}
                  </Typography>
                  )}
                  <SideBarItem
                    title={t("nav.ingredients")}
                    to="/ingrediants"
                    icon={<KebabDiningIcon />}
                  />
                  <SideBarItem
                    title={t("nav.options")}
                    to="/options"
                    icon={<ChecklistIcon />}
                  />
                  {/* <Typography
                  variant="h6"
                  color={colors.grey[300]}
                  sx={{ m: "15px 0 5px 20px" }}
                >
                  Extra
                </Typography>
                <SideBarItem
                  title="Extras"
                  to="/extra"
                  icon={<FastfoodIcon />}
                />
                <SideBarItem
                  title="Boissons"
                  to="/drink"
                  icon={<LiquorIcon />}
                />
                <SideBarItem
                  title="Déssert"
                  to="/desert"
                  icon={<IcecreamIcon />}
                /> */}
                  {!isCollapsed && (
                  <Typography
                    variant="h6"
                    color={colors.grey[300]}
                    sx={{ m: "15px 0 5px 20px" }}
                  >
                    {t("nav.users")}
                  </Typography>
                  )}
                  <SideBarItem
                    title={t("nav.users")}
                    to="/users"
                    icon={<GroupsIcon />}
                  />
                  {!isCollapsed && (
                  <Typography
                    variant="h6"
                    color={colors.grey[300]}
                    sx={{ m: "15px 0 5px 20px" }}
                  >
                    {t("nav.orders")}
                  </Typography>
                  )}
                  <SideBarItem
                    title={t("nav.orders")}
                    to="/history"
                    icon={<ManageSearchIcon />}
                  />
                  {!isCollapsed && (
                  <Typography
                    variant="h6"
                    color={colors.grey[300]}
                    sx={{ m: "15px 0 5px 20px" }}
                  >
                    {t("nav.marketing")}
                  </Typography>
                  )}
                  <SideBarItem
                    title={t("settings.coupon")}
                    to="/coupon"
                    icon={<DiscountIcon />}
                  />
                  <SideBarItem
                    title={t("settings.carousel")}
                    to="/carousel"
                    icon={<CollectionsIcon />}
                  />
              </Box>
            </Menu>
            </SidebarContent>
            {canSeeRestaurants && (
              <SidebarFooter style={{ padding: isCollapsed ? "12px 8px" : "12px 16px" }}>
                {isCollapsed ? (
                  <Tooltip title={t("nav.changeRestaurant")} placement="right">
                    <IconButton
                      onClick={() => setChangeRestaurantOpen(true)}
                      sx={{
                        width: "100%",
                        borderRadius: "8px",
                        border: `1px solid ${colors.redAccent[500]}`,
                        color: colors.redAccent[500],
                      }}
                    >
                      <SwapHorizIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<SwapHorizIcon />}
                    onClick={() => setChangeRestaurantOpen(true)}
                    sx={{ justifyContent: "flex-start" }}
                  >
                    {t("nav.changeRestaurant")}
                  </Button>
                )}
              </SidebarFooter>
            )}
          </ProSidebar>
        </Box>
      </Box>
      <AlertDialog
        open={changeRestaurantOpen}
        handleClose={() => setChangeRestaurantOpen(false)}
        title={t("nav.changeRestaurant")}
        message={t("nav.changeRestaurantConfirm")}
        deleteData={handleChangeRestaurant}
      />
    </Box>
  );
};

export default Sidebar;
